import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Brain, Printer } from 'lucide-react';
import { toast } from 'sonner';

export default function QuadruneBrain() {
  const navigate = useNavigate();

  const printPage = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  const brainParts = [
    {
      title: 'Higher Porpoise',
      subtitle: 'ADVANCED (Prefrontal Cortex)',
      description: 'Our executive brain/co-creative instincts',
      features: 'Wholeness, transcendence, & contribution',
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/30',
      icon: '🐬'
    },
    {
      title: 'Monkey Mind',
      subtitle: 'NEW MAMMALIAN BRAIN (Neocortex)',
      description: 'Our interpretive (often self-deceptive) instincts',
      features: 'Meaning, morality, and make sense',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30',
      icon: '🐵'
    },
    {
      title: 'Furry Li\'l Mammal',
      subtitle: 'OLD MAMMALIAN BRAIN (Limbic System)',
      description: 'Our social instincts',
      features: 'Bonding, status, and play',
      color: 'from-amber-900/30 to-orange-900/30',
      borderColor: 'border-amber-700/30',
      icon: '🦔'
    },
    {
      title: 'Lizard Legacy',
      subtitle: 'REPTILIAN BRAIN (Brain Stem and Cerebellum)',
      description: 'Our physical instincts',
      features: 'Safety, sustenance, sex',
      color: 'from-green-900/30 to-emerald-900/30',
      borderColor: 'border-green-700/30',
      icon: '🦎'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { background: white !important; color: black !important; }
        }
      `}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Our Quadrune Brain</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print for Tabletop
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 53</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Brain className="h-20 w-20 text-purple-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">OUR QUADRUNE BRAIN</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-4xl mx-auto">
              Evolutionary psychology helps us understand our nervous system so we can make more effective choices 
              to create loving relationships. Especially important is how it explains our "mismatched instincts," 
              such as "negativity bias," which create enormous challenges for us.
            </p>
          </div>

          {/* Brain Diagram */}
          <div className="bg-white rounded-lg p-6 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/o01vx7eg_Screenshot%202025-10-31%20at%203.12.54%E2%80%AFAM.png"
              alt="Our Quadrune Brain - Evolutionary layers"
              className="w-full h-auto rounded"
            />
          </div>

          {/* Brain Components */}
          <div className="space-y-6 mb-12">
            {brainParts.map((part, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${part.color} border ${part.borderColor} rounded-lg overflow-hidden`}>
                <div className="bg-slate-900/40 px-6 py-4 border-b border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{part.icon}</div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{part.title}</h3>
                      <p className="text-slate-300 italic text-sm">{part.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-100 text-lg mb-3">{part.description}</p>
                  <p className="text-slate-200 font-semibold">{part.features}</p>
                </div>
              </div>
            ))}\n          </div>\n\n          {/* Description */}\n          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8 mb-8">
            <p className="text-slate-200 leading-relaxed mb-4">
              Our deepest, oldest brain components reflect our ancient "reptilian" heritage—our <span className="font-semibold text-green-300">Lizard Legacy</span>. 
              Next is our paleomammalian brain, the seat of emotions—our <span className="font-semibold text-amber-300">Furry Li'l Mammal</span>. 
              Then our neomammalian <span className="font-semibold text-blue-300">Computer Brain</span>. 
              Finally, our most recently evolved prefrontal lobes—our <span className="font-semibold text-purple-300">Higher Porpoise</span>.
            </p>
            <p className="text-slate-400 text-sm mt-6 italic text-center">\n              These charts can be downloaded at www.thegreatstory.org/charts/triune.html\n            </p>
            <p className="text-slate-400 text-sm mt-2 text-center">\n              Steve Torma • The REAL Center • www.theREALcenter.org • 828-254-5613\n            </p>
          </div>

          {/* Link to Page 54 */}
          <div className="flex justify-center">
            <Button 
              onClick={() => navigate('/workbook/quadrune-evolution')}
              className="bg-purple-600 hover:bg-purple-500 text-white text-lg px-8 py-4"
            >
              Continue to Evolutionary Psychology & Exercises (Page 54) →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
